import { useEffect, useId, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Button } from "./Button";
import { focusablesOf } from "./Popover";

export type DialogProps = {
  open: boolean;
  title: string;
  text?: string | null;
  confirmLabel: string;
  cancelLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
  dataFxmap?: string;
};

export function Dialog({ open, title, text = null, confirmLabel, cancelLabel, danger = false, busy = false, onConfirm, onCancel, children, dataFxmap }: DialogProps) {
  const titleId = useId();
  const textId = useId();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const element = ref.current;

    if (element !== null) {
      const items = focusablesOf(element);
      const preferred = items.find(a => a.dataset.fxmapDialog === "cancel") ?? items[0];

      (preferred ?? element).focus();
    }

    return () => {
      if (previous !== null && document.contains(previous)) previous.focus();
    };
  }, [open]);

  if (!open) return null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onCancel();
      return;
    }

    if (event.key !== "Tab" || ref.current === null) return;

    const items = focusablesOf(ref.current);

    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fxmap-dialog-backdrop" onPointerDown={event => event.target === event.currentTarget && onCancel()}>
      <div ref={ref} className="fxmap-dialog fxmap-card" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={text !== null ? textId : undefined} data-fxmap={dataFxmap ?? "dialog"} tabIndex={-1} onKeyDown={onKeyDown}>
        <h2 id={titleId} className="fxmap-dialog-title fxmap-display">
          {title}
        </h2>
        {text !== null ? (
          <p id={textId} className="fxmap-dialog-text">
            {text}
          </p>
        ) : null}
        {children !== undefined ? <div className="fxmap-dialog-body">{children}</div> : null}
        <div className="fxmap-dialog-actions">
          <Button kind="ghost" label={cancelLabel} onClick={onCancel} data-fxmap-dialog="cancel" disabled={busy} />
          <Button kind={danger ? "danger" : "primary"} label={confirmLabel} onClick={onConfirm} busy={busy} data-fxmap-dialog="confirm" />
        </div>
      </div>
    </div>
  );
}
